import React, { useState, useEffect, useMemo, useRef } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { BarLoader } from 'react-spinners';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { PiDownloadSimple } from 'react-icons/pi';
import { BsFiletypeCsv } from 'react-icons/bs';
import { LiaFileExcel } from 'react-icons/lia';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { RiRefreshLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import './reports.css';
import {
    DATE_API, PAGE_SIZES, DEFAULT_PAGE_SIZE,
    fmtMoney, toDateObj, SelectField, exportRows,
} from './purchaseOrderAdvanced';

const COLS = [
    { header: 'Supplier', field: 'supplier', width: 280 },
    { header: 'Supplier Account Code', field: 'supplierAccountCode', width: 190 },
    { header: 'Approved PO', field: 'approvedPO', width: 140, qty: true },
    { header: 'LPO Amount', field: 'lpoAmount', width: 150, money: true },
    { header: 'Received Amount', field: 'receivedAmount', width: 160, money: true },
    { header: 'Invoiced Amount', field: 'invoicedAmount', width: 160, money: true },
    { header: 'Paid Amount', field: 'paidAmount', width: 150, money: true },
    { header: 'Outstanding', field: 'outStanding', width: 150, money: true },
];
const SUM_FIELDS = ['approvedPO', 'lpoAmount', 'receivedAmount', 'invoicedAmount', 'paidAmount', 'outStanding'];
const EXPORT_CFG = {
    cols: COLS,
    sumFields: SUM_FIELDS,
    title: 'Supplier Spend Report List',
    sheet: 'Supplier Spend Report',
    file: 'Supplier_Spend_Report',
};

const emptyFilters = () => ({
    supplier: null,
    fromDate: moment().format(DATE_API),
    toDate: moment().format(DATE_API),
});

// Query-string prefix of the request object in swagger (supplierReportRequest.*)
const PARAM_PREFIX = 'supplierReportRequest.';

// Empty values are omitted (no filter).
const fetchSupplierSpend = async (apiService, filters, pageNo, pageSize) => {
    const params = new URLSearchParams();
    const add = (key, value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') params.append(`${PARAM_PREFIX}${key}`, String(value).trim());
    };
    add('VendorName', filters.supplier?.label);
    add('FromDate', filters.fromDate);
    add('ToDate', filters.toDate);
    if (pageSize) {
        params.append('PageNo', pageNo || 1);
        params.append('PageSize', pageSize);
    }
    const res = await apiService.getApiParamater('GET_SUPPLIER_SPEND_REPORT', `?${params.toString()}`);
    const body = res?.data;
    // 204 = no rows, not an error
    if (!body || body.statusCode === 204) return { rows: [], pagination: null };
    if (body.statusCode && body.statusCode >= 400) throw new Error(body.message || 'Request failed');
    const list = body.result ?? body.data;
    return { rows: Array.isArray(list) ? list : [], pagination: body.paginationData || null };
};

const SupplierFilterBar = ({ suppliers, onApply, onReset }) => {
    const [f, setF] = useState(emptyFilters());
    const set = (key) => (value) => setF((p) => ({ ...p, [key]: value }));
    const dateError = f.toDate && !f.fromDate ? 'Pick a From date — a To date on its own is ignored.' : '';

    const apply = () => {
        if (dateError) {
            toast.warning(dateError);
            return;
        }
        onApply(f);
    };
    const reset = () => {
        setF(emptyFilters());
        onReset();
    };

    return (
        <div className="gridBg m-0 mb-3 p-3" style={{ borderRadius: 16 }}>
            <div className="row">
                <SelectField label="Supplier" value={f.supplier} onChange={set('supplier')} isClearable placeholder="All"
                    options={(suppliers || []).map((x) => ({ value: x, label: x?.supplierName }))} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">From Date</label>
                    <DatePicker selected={toDateObj(f.fromDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="From"
                        onChange={(d) => set('fromDate')(d ? moment(d).format(DATE_API) : '')} />
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">To Date</label>
                    <DatePicker selected={toDateObj(f.toDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="To" minDate={toDateObj(f.fromDate)}
                        onChange={(d) => set('toDate')(d ? moment(d).format(DATE_API) : '')} />
                    {dateError && <div className="small text-danger">{dateError}</div>}
                </div>
                <div className="col-md-3 col-sm-6 mb-2 d-flex align-items-end gap-2">
                    <div className="mainbtnGry flex-end gap-2 w-50" role="button" tabIndex={0} onClick={apply}
                        onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}>
                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Apply</div>
                    </div>
                    <div className="mainbtnRed flex-end gap-2 w-50" role="button" tabIndex={0} onClick={reset}
                        onKeyDown={(e) => { if (e.key === 'Enter') reset(); }}>
                        <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Reset</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SupplierGrid = ({ rows }) => {
    const colDefs = useMemo(() => COLS.map((c, i) => ({
        headerName: c.header,
        field: c.field,
        width: c.width,
        suppressMovable: true,
        filter: true,
        floatingFilter: true,
        ...(i === 0 ? { pinned: 'left' } : {}),
        ...(c.money || c.qty ? { type: 'rightAligned', headerClass: 'ag-right-aligned-header' } : {}),
        ...(c.money ? { valueFormatter: (p) => fmtMoney(p.value) } : {}),
    })), []);

    return (
        <div className="ag-theme-quartz" style={{ height: 560 }}>
            <AgGridReact
                rowData={rows}
                columnDefs={colDefs}
                getRowStyle={(p) => ({ background: p.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' })}
                suppressMovableColumns
            />
        </div>
    );
};

const SupplierSpendReport = () => {
    const apiService = useRef(new ApiService()).current;
    const reqId = useRef(0);
    const [filters, setFilters] = useState(emptyFilters());
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [suppliers, setSuppliers] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        apiService.get('GET_SUPPLIER_KANOO')
            .then((res) => setSuppliers(res?.data?.result || []))
            .catch((e) => console.error('Error loading suppliers:', e));
    }, [apiService]);

    // Fetches on mount, Apply and page / page-size change only. Stale responses are dropped.
    useEffect(() => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        fetchSupplierSpend(apiService, filters, pageNo, pageSize)
            .then(({ rows: r, pagination: p }) => {
                if (id !== reqId.current) return;
                setRows(r);
                setPagination(p);
            })
            .catch((e) => {
                if (id !== reqId.current) return;
                setRows([]);
                setPagination(null);
                setError(e?.message || 'Unable to load the report.');
            })
            .finally(() => { if (id === reqId.current) setLoading(false); });
    }, [apiService, filters, pageNo, pageSize]);

    const handleApply = (f) => { setFilters({ ...f }); setPageNo(1); };
    const handleReset = () => { setFilters(emptyFilters()); setPageNo(1); };
    // Export covers every matching row: one request with PageNo 1 and PageSize = total records.
    const handleExport = async (type) => {
        const total = pagination?.totalRecords || rows.length;
        if (!total) return;
        setExporting(true);
        try {
            const { rows: all } = await fetchSupplierSpend(apiService, filters, 1, total);
            exportRows(all, type, EXPORT_CFG);
        } catch (e) {
            toast.error(e?.message || 'Unable to export the report.');
        } finally {
            setExporting(false);
        }
    };
    const handlePageSize = (e) => { setPageSize(Number(e.target.value)); setPageNo(1); };

    return (
        <div className="p-4">
            <SupplierFilterBar suppliers={suppliers} onApply={handleApply} onReset={handleReset} />

            <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                <div className="mainInvHead center">Supplier Spend Report</div>
                <div className="ms-auto">
                    <select className="form-select form-select-sm" aria-label="Page size" value={pageSize} onChange={handlePageSize}>
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
                <div className="mainbtnGry flex-end gap-2">
                    <Dropdown isOpen={exportOpen} toggle={() => setExportOpen(!exportOpen)}>
                        <DropdownToggle tag="span" aria-expanded={exportOpen} role="button" aria-label="Export">
                            <PiDownloadSimple size={25} />
                        </DropdownToggle>
                        <DropdownMenu className="mt-3">
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('csv')} className="gap-3 d-flex center"><BsFiletypeCsv /><div>Export CSV</div></DropdownItem>
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('xlsx')} className="gap-3 d-flex center"><LiaFileExcel /><div>Export Excel</div></DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>

            <div className="gridshad">
                {loading ? (
                    <div className="center p-5"><BarLoader color="#6E5199" height={3} width={200} /></div>
                ) : error ? (
                    <div className="text-danger p-4" role="alert">{error}</div>
                ) : !rows.length ? (
                    <div className="text-muted p-4">No supplier records found.</div>
                ) : (
                    <SupplierGrid rows={rows} />
                )}
                {pagination && <Pagination pagination={pagination} getItems={(p) => setPageNo(p)} />}
            </div>
        </div>
    );
};

export default SupplierSpendReport;
